import React, { useState, useEffect, useCallback } from 'react';
import { Shield, RefreshCw, Search, User, Clock, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import api from '@/lib/api';

const AuditLogPage = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [actionFilter, setActionFilter] = useState('');
  const limit = 25;

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = { limit, skip: page * limit };
      if (actionFilter) params.action = actionFilter;
      const response = await api.get('/admin/audit-log', { params });
      setLogs(response.data?.items || []);
      setTotal(response.data?.total || 0);
    } catch (error) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [page, actionFilter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const formatTime = (ts) => {
    try {
      return new Date(ts).toLocaleString('en-US', {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit'
      });
    } catch { return ts; }
  };

  const getActionBadge = (action) => {
    if (action.includes('login')) return <Badge className="bg-blue-900/30 text-blue-300 text-xs">{action}</Badge>;
    if (action.includes('register')) return <Badge className="bg-emerald-900/30 text-emerald-300 text-xs">{action}</Badge>;
    if (action.includes('create')) return <Badge className="bg-emerald-900/30 text-emerald-300 text-xs">{action}</Badge>;
    if (action.includes('delete')) return <Badge className="bg-amber-900/30 text-amber-300 text-xs">{action}</Badge>;
    if (action.includes('run') || action.includes('completed')) return <Badge className="bg-purple-900/30 text-purple-300 text-xs">{action}</Badge>;
    return <Badge variant="secondary" className="text-xs">{action}</Badge>;
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Audit Log</h1>
          <p className="text-slate-300">Track all system actions and events</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={async () => {
            try {
              const params = actionFilter ? `?action=${encodeURIComponent(actionFilter)}` : '';
              const r = await api.get(`/admin/audit-log/export${params}`, { responseType: 'blob' });
              const blob = new Blob([r.data], { type: 'text/csv' });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a'); a.href = url;
              a.download = `dotbox_audit_log_${new Date().toISOString().slice(0,10)}.csv`;
              document.body.appendChild(a); a.click(); document.body.removeChild(a);
              window.URL.revokeObjectURL(url);
              toast.success('Audit log exported');
            } catch { toast.error('Export failed'); }
          }}>
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => { setPage(0); fetchLogs(); }} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-4">
        <CardContent className="p-3">
          <div className="flex items-center gap-3">
            <Search className="h-4 w-4 text-gray-400" />
            <Input
              placeholder="Filter by action (e.g., login, create, delete)..."
              value={actionFilter}
              onChange={e => { setActionFilter(e.target.value); setPage(0); }}
              className="flex-1 h-8 text-sm"
            />
            <span className="text-sm text-gray-500">{total} events</span>
          </div>
        </CardContent>
      </Card>

      {/* Log Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-[#0A1024]">
                  <th className="text-left p-3 font-medium text-slate-300">Time</th>
                  <th className="text-left p-3 font-medium text-slate-300">User</th>
                  <th className="text-left p-3 font-medium text-slate-300">Action</th>
                  <th className="text-left p-3 font-medium text-slate-300">Resource</th>
                  <th className="text-left p-3 font-medium text-slate-300">Details</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={`audit-skeleton-${i}`} className="border-b">
                      <td className="p-3"><Skeleton className="h-4 w-24" /></td>
                      <td className="p-3"><Skeleton className="h-4 w-32" /></td>
                      <td className="p-3"><Skeleton className="h-4 w-20" /></td>
                      <td className="p-3"><Skeleton className="h-4 w-16" /></td>
                      <td className="p-3"><Skeleton className="h-4 w-40" /></td>
                    </tr>
                  ))
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-8 text-center text-gray-400">
                      <Shield className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                      No audit log entries yet
                    </td>
                  </tr>
                ) : (
                  logs.map((log, idx) => (
                    <tr key={log.timestamp ? `${log.timestamp}-${idx}` : `log-${idx}`} className="border-b hover:bg-[#0A1024]">
                      <td className="p-3 text-gray-500 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatTime(log.timestamp)}
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-1.5">
                          <User className="h-3 w-3 text-gray-400" />
                          <span className="text-slate-200 text-xs font-mono">{log.user_email || log.user_id?.slice(0, 8) + '...'}</span>
                        </div>
                      </td>
                      <td className="p-3">{getActionBadge(log.action)}</td>
                      <td className="p-3 text-gray-500 text-xs">{log.resource_type}</td>
                      <td className="p-3 text-slate-300 text-xs max-w-xs truncate">{log.details}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-3 border-t">
              <span className="text-sm text-gray-500">
                Page {page + 1} of {totalPages}
              </span>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogPage;
